import { useEffect, type JSX } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { type SignUpRefusalKind, signUpRefusalIn } from '@profiler/shared'
import { useSignIn } from '../auth'
import { BrandMark, PARENT_MARK_SRC, PARENT_NAME, PRODUCT_NAME, PRODUCT_TAGLINE } from '../components/Brand'
import { BRAND } from '../theme'
import { ErrorPanel } from '../components/States'
import { usePageTitle } from '../usePageTitle'

/**
 * The description Cognito sent back, out of whatever the sign-in library passed on.
 *
 * react-oidc-context does not pass the Error it caught. It passes a plain object
 * shaped like one, `{ name, message, innerError, source }`, which fails
 * `instanceof Error`. The description Cognito wrote is on `innerError`, and the
 * message repeats it.
 */
export function callbackDescription(error: unknown): string | null {
  if (typeof error === 'string') return error
  if (!error || typeof error !== 'object') return null
  const { message, innerError } = error as { message?: unknown; innerError?: unknown }
  const inner = innerError as { error_description?: unknown } | null | undefined
  if (typeof inner?.error_description === 'string') return inner.error_description
  return typeof message === 'string' ? message : null
}

/** An Error for the panel, so a plain object reads as its message rather than "[object Object]". */
function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(callbackDescription(error) ?? String(error))
}

const REFUSAL_TITLE: Record<SignUpRefusalKind, string> = {
  notInvited: 'You have not been invited yet',
  notStaff: 'This account cannot use Quarry',
  noEmail: 'Quarry could not tell who you are',
  unavailable: 'Try again in a minute',
}

/**
 * Take a failed sign-in's `error`, `error_description` and `state` out of the address.
 *
 * The library has already used the stored state that `state` pointed at. Left
 * in place, a reload tries the callback again and fails with "No matching state
 * found", which hides the real reason behind one nobody can act on.
 */
function forgetFailedCallback(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('error')) return
  for (const key of ['error', 'error_description', 'state']) url.searchParams.delete(key)
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
}

/** What Quarry does, in the order it does it. Kept to what the product actually guarantees. */
const PROMISES = [
  'Bring a list of target companies',
  'Quarry finds the people who decide, and checks every lead',
  'Each email and phone number comes with its source',
] as const

/** The Microsoft four-square logo, drawn to the size of the button icon. */
function MicrosoftLogo(): JSX.Element {
  return (
    <Box component="svg" viewBox="0 0 21 21" aria-hidden sx={{ width: 18, height: 18, display: 'block' }}>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </Box>
  )
}

/**
 * The mark, drawn large: a quarry in cross-section.
 *
 * Horizontal strata in the rock, a pit cut into it in benches, and one lit dot
 * on the floor of the cut. It is the same idea as the 32px glyph, so the screen
 * explains the mark instead of repeating it.
 */
function QuarrySection(): JSX.Element {
  const surface = 'M0 118H132V166H212V214H268V262H352V214H408V166H488V118H640'
  return (
    <Box
      component="svg"
      viewBox="0 0 640 360"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden
      sx={{
        position: 'absolute',
        insetInline: 0,
        bottom: 0,
        width: '100%',
        height: '46%',
        display: 'block',
        '@media (prefers-reduced-motion: no-preference)': {
          '& .seam-glow': { animation: 'seamGlow 4.5s ease-in-out infinite', transformOrigin: '310px 244px' },
        },
        '@keyframes seamGlow': {
          '0%, 100%': { opacity: 0.55, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.12)' },
        },
      }}
    >
      <defs>
        <clipPath id="quarry-rock">
          <path d={`${surface}V360H0Z`} />
        </clipPath>
        <radialGradient id="quarry-glow">
          <stop offset="0" stopColor="#fff" stopOpacity="0.45" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g clipPath="url(#quarry-rock)">
        <rect width="640" height="360" fill="#fff" opacity="0.05" />
        {Array.from({ length: 13 }, (_, i) => (
          <line
            key={i}
            x1="0"
            x2="640"
            y1={134 + i * 18}
            y2={134 + i * 18}
            stroke="#fff"
            strokeOpacity={0.1}
          />
        ))}
      </g>
      <path
        d={surface}
        fill="none"
        stroke="#fff"
        strokeOpacity="0.55"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle className="seam-glow" cx="310" cy="244" r="46" fill="url(#quarry-glow)" />
      <circle cx="310" cy="244" r="9" fill="#fff" />
    </Box>
  )
}

/** The left half on a wide screen: who this is, what it does, and the mark drawn out. */
function BrandPanel(): JSX.Element {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
        color: '#fff',
        px: { md: 6, lg: 8 },
        pt: { md: 6, lg: 7 },
        pb: 6,
        background: `radial-gradient(120% 90% at 0% 0%, ${BRAND.light}33 0%, transparent 55%), linear-gradient(160deg, ${BRAND.deep} 0%, #084B7C 55%, #06385D 100%)`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <BrandMark size={40} />
        <Stack sx={{ lineHeight: 1.1 }}>
          <Typography component="span" sx={{ fontSize: 22, fontWeight: 650, letterSpacing: '-0.015em' }}>
            {PRODUCT_NAME}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              opacity: 0.75,
            }}
          >
            by {PARENT_NAME}
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={4} sx={{ mt: { md: 8, lg: 12 }, maxWidth: 520, position: 'relative', zIndex: 1 }}>
        <Typography
          component="p"
          sx={{
            fontSize: { md: '2.25rem', lg: '2.75rem' },
            lineHeight: 1.12,
            fontWeight: 650,
            letterSpacing: '-0.025em',
            textWrap: 'balance',
          }}
        >
          {PRODUCT_TAGLINE}
        </Typography>
        <Stack component="ul" spacing={1.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {PROMISES.map((line) => (
            <Stack component="li" key={line} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  mt: '1px',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  bgcolor: 'rgba(255,255,255,0.14)',
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 15 }} />
              </Box>
              <Typography sx={{ fontSize: '1rem', lineHeight: 1.45, opacity: 0.92 }}>{line}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <QuarrySection />
    </Box>
  )
}

export function SignInPage({ error }: Readonly<{ error?: unknown }>): JSX.Element {
  usePageTitle('Sign in')

  const { signIn, label, microsoft } = useSignIn()
  const refusal = error ? signUpRefusalIn(callbackDescription(error)) : null

  useEffect(() => {
    if (error) forgetFailedCallback()
  }, [error])

  /*
   * A refusal from the sign-up check gets its own words and no red. It is the
   * product answering, not failing, and the fix is somebody else's click.
   * Everything else is still an error.
   */
  const problem = (): JSX.Element | null => {
    if (refusal) {
      return (
        <Alert severity="warning" sx={{ width: '100%' }}>
          <AlertTitle>{REFUSAL_TITLE[refusal.kind]}</AlertTitle>
          {refusal.message}
          {refusal.kind === 'notInvited' ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              To use a different Microsoft account, sign out of Microsoft first or open a private window, then
              sign in again.
            </Typography>
          ) : null}
        </Alert>
      )
    }
    return error ? <ErrorPanel error={asError(error)} /> : null
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 1fr)' },
        bgcolor: 'background.paper',
      }}
    >
      <BrandPanel />

      <Box
        component="main"
        sx={{ display: 'flex', flexDirection: 'column', px: { xs: 3, sm: 6 }, py: { xs: 4, sm: 6 } }}
      >
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <Stack spacing={3} sx={{ width: '100%', maxWidth: 380 }}>
            <Box sx={{ display: { md: 'none' } }}>
              <BrandMark size={48} />
            </Box>
            <Stack spacing={1}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  fontWeight: 650,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                }}
              >
                Sign in to {PRODUCT_NAME}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.975rem', lineHeight: 1.5 }}>
                Use your {PARENT_NAME} Microsoft account. Access is granted by your administrator.
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ display: { md: 'none' }, fontSize: '0.975rem', lineHeight: 1.5, pt: 0.5 }}
              >
                {PRODUCT_TAGLINE}
              </Typography>
            </Stack>

            {problem()}

            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={microsoft ? <MicrosoftLogo /> : <LoginOutlinedIcon />}
              onClick={signIn}
              sx={(theme) => ({
                height: 50,
                fontSize: '1rem',
                fontWeight: 600,
                color: 'text.primary',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(24,33,38,0.06)',
                '& .MuiButton-startIcon': { mr: 1.5 },
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              })}
            >
              {label}
            </Button>
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ pt: 4, color: 'text.secondary' }}
        >
          <Box
            component="img"
            src={PARENT_MARK_SRC}
            alt=""
            aria-hidden
            sx={{ width: 16, height: 16, display: 'block' }}
          />
          <Typography variant="caption">
            {PRODUCT_NAME} is an internal tool of {PARENT_NAME}
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
}
